/**
 * The halves of a count-in-a-window condition that ride in `meta` — read back out of
 * a serialized document, and checked before it can be saved.
 *
 * Checked here because nothing else on the save path can: the query builder validates
 * a condition's value and has no view of its meta, so a count with an empty meta is a
 * complete condition as far as it is concerned.
 */
import { GROUP_TYPE_KEYS } from '@pushly/pquery-core'
import type { PQueryBuilderField } from '@pushly/models/lib/interfaces/pquery-builder-field'

/** The group keys the wire format defines, from the engine rather than a copy kept here. */
const GROUP_KEYS = [...GROUP_TYPE_KEYS]

/**
 * The renderers whose condition counts events matching something the reader names.
 *
 * On these surfaces the condition's value is only half the question — "at least
 * three" is not a condition until it says three of what — and the other half rides
 * in `meta.expression`. Without it the engine counts nothing and the segment
 * silently matches no one, which is why an empty one cannot be saved.
 *
 * The count surfaces that are absent from this set are absent deliberately: they
 * count every event of their kind, so no expression is the correct condition there.
 */
const EXPRESSION_REQUIRED_INPUT_KEYS: ReadonlySet<string> = new Set([
    'keyword-clicked',
    'page-tag-visited',
    'page-url-visited',
])

/**
 * The renderers whose condition counts over a window.
 *
 * The window is `meta.scope`, and the engine reads a missing one as no window at all
 * rather than as whatever period the row displayed — the count is taken over the
 * subscriber's whole history, so the segment matches a wider audience than the one on
 * screen and nothing about the saved row says so.
 *
 * A superset of {@link EXPRESSION_REQUIRED_INPUT_KEYS}: a surface that counts every
 * event of its kind still counts them over a window. Every renderer segmentation
 * supplies is one of these, so a new one has to be named here too.
 */
export const WINDOW_REQUIRED_INPUT_KEYS: ReadonlySet<string> = new Set([
    ...EXPRESSION_REQUIRED_INPUT_KEYS,
    'frequency-window',
])

/**
 * The fields whose conditions leave `metaKey` unanswered, by label and in document
 * order, each named once however many rows carry it.
 *
 * Whitespace counts as unanswered: a window or an expression made of spaces is not
 * something the engine can read, and reporting it as present would let it save.
 */
function findConditionsMissingMeta(
    doc: unknown,
    fields: PQueryBuilderField[],
    inputKeys: ReadonlySet<string>,
    metaKey: 'expression' | 'scope',
): string[] {
    const byKey = new Map(fields.map((field) => [field.key, field]))
    const missing: string[] = []

    const walk = (node: unknown): void => {
        if (!node || typeof node !== 'object') return

        const record = node as Record<string, unknown>
        const group = GROUP_KEYS.map((key) => record[key]).find(Array.isArray)

        if (group) {
            ;(group as unknown[]).forEach(walk)
            return
        }

        Object.entries(record).forEach(([property, condition]) => {
            const field = byKey.get(property)
            if (!field?.inputKey || !inputKeys.has(field.inputKey)) return

            const meta = (condition as { meta?: Record<string, unknown> } | null)?.meta
            const answer = typeof meta?.[metaKey] === 'string' ? (meta[metaKey] as string).trim() : ''

            if (!answer && !missing.includes(field.label)) missing.push(field.label)
        })
    }

    walk(doc)

    return missing
}

/**
 * The fields a document counts against without saying what to count, by label and
 * in document order, each named once however many rows carry it.
 */
export function findConditionsMissingExpression(doc: unknown, fields: PQueryBuilderField[]): string[] {
    return findConditionsMissingMeta(doc, fields, EXPRESSION_REQUIRED_INPUT_KEYS, 'expression')
}

/**
 * The fields a document counts over no window at all, by label and in document order,
 * each named once however many rows carry it.
 */
export function findConditionsMissingWindow(doc: unknown, fields: PQueryBuilderField[]): string[] {
    return findConditionsMissingMeta(doc, fields, WINDOW_REQUIRED_INPUT_KEYS, 'scope')
}

/**
 * Every distinct thing a document's conditions on `property` say they match on.
 *
 * What a stored condition names is not guaranteed to be in whatever page of a long
 * vocabulary a control has fetched, so a control that offers only what it fetched
 * would show the reader a blank where their own choice should be. Reading it back
 * out of the document is the only source that cannot be missing it.
 */
export function collectExpressions(doc: unknown, property: string): string[] {
    const found: string[] = []

    const walk = (node: unknown): void => {
        if (!node || typeof node !== 'object') return

        const record = node as Record<string, unknown>
        const group = GROUP_KEYS.map((key) => record[key]).find(Array.isArray)

        if (group) {
            ;(group as unknown[]).forEach(walk)
            return
        }

        const condition = record[property] as { meta?: { expression?: unknown } } | undefined
        const expression = condition?.meta?.expression

        if (typeof expression === 'string' && expression.trim() && !found.includes(expression)) {
            found.push(expression)
        }
    }

    walk(doc)

    return found
}

/**
 * How a blocked submit explains the gap, or `null` when there is none.
 *
 * Names the fields rather than the rows: nothing here can point at a row, and a
 * reader who is told which condition type is incomplete can find it.
 */
export function describeMissingExpressions(labels: string[]): string | null {
    if (!labels.length) return null

    return `These conditions need a value to match on before the segment can be saved: ${labels.join(', ')}.`
}

/**
 * How a submit blocked for a missing window explains the gap, or `null` when there is
 * none. Names the fields rather than the rows, for the same reason as above.
 */
export function describeMissingWindows(labels: string[]): string | null {
    if (!labels.length) return null

    return `These conditions need a time frame before the segment can be saved: ${labels.join(', ')}.`
}
